/**
 * TicketedEventCategory Entity Schemas
 *
 * Curated, seeded, image-backed event category. Single-select per event.
 * Categories are stored as data (not a code enum) with a nullable school scope
 * so per-school custom categories/images become a later data + small-UI
 * addition, not a schema migration (R21–R23, plan KTD5).
 */

import { z } from 'zod'

/**
 * The client-facing category DTO. `id` is the CUID `publicId`. `schoolId` is
 * intentionally NOT exposed — the client only distinguishes categories by
 * their public identity + presentation fields.
 */
export const TicketedEventCategorySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  imageUrl: z.string(),
  sortOrder: z.number().int()
})
export type TicketedEventCategory = z.infer<typeof TicketedEventCategorySchema>

/**
 * The validated shape of a seed row (plan KTD5 — seeds go through the Zod
 * validated write path, never raw `db.create`). Global rows carry no schoolId
 * (it is set to null at insert time).
 */
export const TicketedEventCategorySeedInputSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-z0-9-]+$/, 'slug must be kebab-case'),
  name: z.string().min(1).max(255),
  imageUrl: z.string().url().max(1000),
  sortOrder: z.number().int().min(0)
})
export type TicketedEventCategorySeedInput = z.infer<typeof TicketedEventCategorySeedInputSchema>
